var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Children, createRef, isValidElement, useContext, useEffect, useRef, useState } from 'react';
import { createRoutesFromElements, matchRoutes, Route, UNSAFE_LocationContext, UNSAFE_RouteContext, useLocation, useRoutes } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { FlowContext, useFlow } from '../context/FlowContext';
/** How far, and along which property, a view travels when it comes in or goes out. */
const TRANSFORMS = {
    'slide': { fn: 'translateX', out: '100%' },
    'vertical-slide': { fn: 'translateY', out: '100%' },
    'rotate': { fn: 'rotateY', out: '180deg' }
};
const isRouteElement = (element) => {
    return isValidElement(element) && element.type === Route;
};
/**
 * The payment-return view is reached only by a redirect the payment provider makes in its own
 * tab, never by a navigation inside this app - so `flow.busy`/`transitioning` describe state this
 * view had no part in raising. Whatever caused it to get stuck, a veil with a spinner on top of
 * this view's own close-tab button is always wrong: there is no transition for it to belong to.
 */
const NEVER_VEILED_PATHS = ['oa_widget_return/:paymentId', 'oa_widget_payment1/:paymentId'];
// from useRoutes:
// https://github.com/remix-run/react-router/blob/f3d3e05ec00c6950720930beaf74fecbaf9dc5b6/packages/react-router/lib/hooks.tsx#L302
const useNextPath = (pathname = '') => {
    const { matches: parentMatches } = useContext(UNSAFE_RouteContext);
    const routeMatch = parentMatches[parentMatches.length - 1];
    const parentPathnameBase = routeMatch ? routeMatch.pathnameBase : '/';
    return parentPathnameBase === '/'
        ? pathname
        : pathname.slice(parentPathnameBase.length) || '/';
};
const getMatch = (routes, pathname) => {
    const matches = matchRoutes(routes, pathname);
    if (matches === null) {
        throw new Error(`Route ${pathname} does not match`);
    }
    const index = routes.findIndex((route) => {
        return matches.some((match) => match.route === route);
    });
    return { index, route: routes[index] };
};
const ViewContext = ({ location, flow, children }) => (_jsx(UNSAFE_LocationContext.Provider, { value: { location, navigationType: 'POP' }, children: _jsx(FlowContext.Provider, { value: flow, children: children }) }));
const SlideRoutes = (props) => {
    var _a, _b, _c, _d, _e;
    const { animation = 'slide', duration = 200, timing = 'ease', compare, children } = props;
    // routes
    //@ts-ignore
    const routeElements = Children.map(children, (child) => {
        if (!isRouteElement(child)) {
            return child;
        }
        const _a = child.props, { element } = _a, restProps = __rest(_a, ["element"]);
        if (!element) {
            return child;
        }
        const nodeRef = createRef();
        const newElement = (_jsx("div", { className: "item", ref: nodeRef, children: element }));
        return Object.assign(Object.assign({}, child), { props: Object.assign(Object.assign({}, restProps), { element: newElement }) });
    });
    const routes = createRoutesFromElements(routeElements);
    if (compare) {
        routes.sort(compare);
    }
    const location = useLocation();
    const flow = useFlow();
    // the view on screen, which is only allowed to become the new one once that one has rendered
    const [displayed, setDisplayed] = useState(location);
    const [phase, setPhase] = useState('idle');
    const nextPath = useNextPath(location.pathname);
    const shownPath = useNextPath(displayed.pathname);
    const nextMatch = getMatch(routes, nextPath);
    const shownMatch = getMatch(routes, shownPath);
    // both trees are built on every render - hooks cannot be called conditionally - but the one
    // for the view being left is only rendered while a transition is running
    const leavingRoutes = useRoutes(routes, displayed);
    const enteringRoutes = useRoutes(routes, location);
    const changing = nextMatch.index !== shownMatch.index;
    /**
     * The flow as it was when the view on screen was the current one.
     *
     * This cannot wait for the router. The flow advances first and the navigation follows a frame
     * or two later, and in between the view that is still mounted already renders against the
     * route that is replacing it - which is the spinner the visitor sees before anything has even
     * started moving. So the moment `flow.currentRoute` moves off the snapshot, the view on
     * screen is treated as leaving: it keeps the flow it had, and the overlay goes up.
     */
    const snapshot = useRef(flow);
    const advancing = snapshot.current.currentRoute !== flow.currentRoute;
    const consistent = ((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route) === ((_b = flow.currentView) === null || _b === void 0 ? void 0 : _b.route.route);
    if (!changing && !advancing && consistent) {
        snapshot.current = flow;
    }
    // on screen, and on its way out: either the flow has moved on, or the router has
    const transitioning = changing || advancing;
    // the view is not going anywhere yet, but it is waiting on something before it can - the
    // summary on prepareCart, the sign-in on the e-mail check. Same veil, raised over the view
    // that is still the current one, so pressing the button visibly does something.
    const veiled = (transitioning || flow.busy) && !NEVER_VEILED_PATHS.includes(shownMatch.route.path);
    const direction = !changing
        ? 'undirected'
        : (nextMatch.index > shownMatch.index ? 'forward' : 'back');
    // a navigation within the same view - a subroute, a changed parameter - is not a transition;
    // the view stays where it is and simply reads the new location
    useEffect(() => {
        if (!changing && displayed !== location) {
            setDisplayed(location);
        }
    }, [changing, displayed, location]);
    // give the new view a frame to mount and a frame to paint before anything moves
    useEffect(() => {
        if (!changing)
            return;
        setPhase('preparing');
        let second = 0;
        const first = requestAnimationFrame(() => {
            second = requestAnimationFrame(() => setPhase('sliding'));
        });
        return () => {
            cancelAnimationFrame(first);
            cancelAnimationFrame(second);
        };
    }, [changing, nextMatch.index]);
    // and once they have travelled, the view that left is dropped and the flow of the view that
    // arrived becomes the one to hold on to
    useEffect(() => {
        if (phase !== 'sliding')
            return;
        const timer = setTimeout(() => {
            snapshot.current = flow;
            setDisplayed(location);
            setPhase('idle');
        }, duration);
        return () => clearTimeout(timer);
    }, [phase, duration, location, flow]);
    const { fn, out } = (_c = TRANSFORMS[animation]) !== null && _c !== void 0 ? _c : TRANSFORMS.slide;
    const away = direction === 'back' ? `${fn}(${out})` : `${fn}(-${out})`;
    const waiting = direction === 'back' ? `${fn}(-${out})` : `${fn}(${out})`;
    const item = Object.assign({ gridArea: '1 / 1 / 2 / 2', width: '100%' }, (animation === 'rotate' && { backfaceVisibility: 'hidden' }));
    const moving = phase === 'sliding' ? { transition: `transform ${duration}ms ${timing}` } : {};
    return (_jsx(Box, { sx: {
            transition: 'all 0.2s linear',
            position: 'relative'
        }, children: _jsxs(Box, { className: `slide-routes ${animation}`, sx: Object.assign({ display: 'grid', overflow: 'hidden' }, (animation === 'rotate' && { perspective: '2000px' })), children: [_jsxs(Box, { className: transitioning ? 'item leaving' : 'item', sx: Object.assign(Object.assign(Object.assign({}, item), moving), { position: 'relative', transform: phase === 'sliding' ? away : 'none' }), children: [_jsx(ViewContext, { location: displayed, flow: transitioning ? snapshot.current : flow, children: leavingRoutes }), veiled && (_jsx(Box, { sx: {
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                    ? 'rgba(0, 0, 0, 0.45)'
                                    : 'rgba(255, 255, 255, 0.55)',
                                zIndex: 1100
                            }, children: _jsx(CircularProgress, {}) }))] }, (_d = shownMatch.route.path) !== null && _d !== void 0 ? _d : shownMatch.index), changing && (_jsx(Box, { className: "item entering", sx: Object.assign(Object.assign(Object.assign({}, item), moving), { transform: phase === 'sliding' ? 'none' : waiting }), children: _jsx(ViewContext, { location: location, flow: flow, children: enteringRoutes }) }, (_e = nextMatch.route.path) !== null && _e !== void 0 ? _e : nextMatch.index))] }) }));
};
export default SlideRoutes;
